import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, GitBranch, User, Compass, Users, Car, Truck } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useSession } from "@/context/SessionContext";
import { ALL_NAV } from "@/lib/nav";

const EMPTY = { companies: [], branches: [], users: [], customers: [], vendors: [], vehicles: [], fleets: [] };
const MASTER_GROUPS = [
  ["vehicles", "Vehicles", Car, "/vehicles", (d) => `${d.registration_number} · ${d.make} ${d.model}`],
  ["customers", "Customers", Users, "/customers", (d) => `${d.name} · ${d.code}${d.phone ? ` · ${d.phone}` : ""}`],
  ["fleets", "Fleets", Truck, "/fleets", (d) => `${d.name} · ${d.code}`],
  ["vendors", "Vendors", Building2, "/vendors", (d) => `${d.name} · ${d.code}`],
];

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(EMPTY);
  const { api, can, setCompany } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(EMPTY);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        setRes({ ...EMPTY, ...(await api.get("/search", { params: { q: q.trim() } })).data });
      } catch {
        setRes(EMPTY);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const pages = ALL_NAV.filter((i) => (!i.perm || can(i.perm)) && i.label.toLowerCase().includes(q.toLowerCase())).slice(0, 6);

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-button" className="flex items-center gap-2 rounded-md border bg-white px-3 py-1.5 text-sm text-muted-foreground hover:border-[hsl(var(--gold)/0.6)] transition-colors md:w-64">
        <Search className="h-4 w-4" />
        <span className="hidden md:inline flex-1 text-left">Search…</span>
        <kbd className="hidden md:inline text-[10px] font-mono-g border rounded px-1.5 py-0.5 bg-secondary">⌘K</kbd>
      </button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages, branches, users…" value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          {pages.length > 0 && (
            <CommandGroup heading="Pages">
              {pages.map((p) => (
                <CommandItem key={p.key} value={`page-${p.label}`} onSelect={() => go(p.path)} data-testid={`search-page-${p.key}`}>
                  <Compass className="h-4 w-4 mr-2" />{p.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {res.companies.length > 0 && (
            <CommandGroup heading="Companies">
              {res.companies.map((c) => (
                <CommandItem key={c.id} value={`${q} company ${c.name} ${c.code} ${c.id}`} onSelect={() => { setCompany(c.id); go("/"); }}>
                  <Building2 className="h-4 w-4 mr-2" />{c.name} <span className="ml-2 text-xs text-muted-foreground">{c.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {res.branches.length > 0 && (
            <CommandGroup heading="Branches">
              {res.branches.map((b) => (
                <CommandItem key={b.id} value={`${q} branch ${b.name} ${b.code} ${b.id}`} onSelect={() => go("/admin/branches")}>
                  <GitBranch className="h-4 w-4 mr-2" />{b.name} <span className="ml-2 text-xs text-muted-foreground">{b.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {MASTER_GROUPS.map(([key, heading, Icon, path, label]) => res[key]?.length > 0 && (
            <CommandGroup key={key} heading={heading}>
              {res[key].map((d) => (
                <CommandItem key={d.id} value={`${q} ${key} ${label(d)} ${d.id}`} onSelect={() => go(`${path}/${d.id}`)} data-testid={`search-${key}-${d.id}`}>
                  <Icon className="h-4 w-4 mr-2" />{label(d)}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {res.users.length > 0 && (
            <CommandGroup heading="Users">
              {res.users.map((u) => (
                <CommandItem key={u.id} value={`${q} user ${u.name} ${u.email} ${u.id}`} onSelect={() => go("/admin/users")}>
                  <User className="h-4 w-4 mr-2" />{u.name} <span className="ml-2 text-xs text-muted-foreground">{u.email}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
